import { Avatar, Button, Skeleton, Stack, Typography } from '@mui/material'
import { TFunction } from 'i18next'
import { useMemo } from 'react'
import { Fade, Icons, SkeletonText } from 'utoniq-ui'

import { ConsoleCampaignDetailQuery, Maybe } from '@/clients/utoniq-core/schema'
import { BottomSafeArea, Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import type { ConsoleCampaignItem } from '@/components/organisms'
import {
  CampaignRankImage,
  ConsoleCampaignItemBox,
  FileType,
} from '@/components/organisms'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

import { CampaignItemForm, CampaignItemFormProps } from './CampaignItemForm'

export * from './fragments'

export type State = {
  loading: boolean
  uploading: boolean
  artistId: string
  eyecatch: string
  campaign: Maybe<ConsoleCampaignDetailQuery['campaign']>
  consoleCampaignItems: ConsoleCampaignItem[]
  hasAvatarImage: boolean
}

export type Action = {
  onUploadFile: CampaignItemFormProps['onUploadFile']
  onCampaignApply: CampaignItemFormProps['onSubmit']
}

export const ConsoleCampaignDetailTemplate: Template<State, Action> = ({
  state,
  onUploadFile,
  onCampaignApply,
}) => {
  const { routerNames, routerPages } = useRouter()

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleCampaignDetail} />

      <ConsoleBaseLayout
        customNavigation={state.hasAvatarImage ? <FixedButtonCell /> : null}
        header={{
          back: true,
          defaultBackPage: routerPages.ConsoleCampaigns,
          title: state.campaign?.title,
        }}
      >
        <CampaignRankImage
          thumbnail={state.campaign?.thumbnail}
          loading={state.loading}
        />
        {state.loading || !state.campaign ? (
          <>
            <CampaignDescriptionSkeletonCell />
            <CampaignItemsSkeletonCell />
          </>
        ) : (
          <>
            <CampaignDescriptionCell
              campaign={state.campaign}
              eyecatch={state.eyecatch}
              hasAvatarImage={state.hasAvatarImage}
            />
            {state.hasAvatarImage && (
              <CampaignItemForm
                onSubmit={onCampaignApply}
                items={state.consoleCampaignItems}
                uploading={state.uploading}
                onUploadFile={onUploadFile}
              />
            )}
          </>
        )}
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type CampaignDescriptionCellProps = {
  campaign: State['campaign']
  eyecatch: State['eyecatch']
  hasAvatarImage: State['hasAvatarImage']
}
const CampaignDescriptionCell: Cell<CampaignDescriptionCellProps> = ({
  campaign,
  eyecatch,
  hasAvatarImage,
}) => {
  const { t } = useTranslation('consoleCampaignDetail')
  return (
    <CellFrame sx={{ px: 5 }}>
      <Stack sx={{ pt: 5, pb: 4 }}>
        <Typography variant="h4">{campaign?.title}</Typography>
        <Typography variant="subtitle1" sx={{ pt: 4 }}>
          {t('contestOverview')}
        </Typography>
        <Typography variant="body2" sx={{ pt: 1 }}>
          {campaign?.description}
        </Typography>
        <Typography variant="subtitle1" sx={{ pt: 4 }}>
          {t('dateTime')}
        </Typography>
        <Typography variant="body2" sx={{ pt: 1 }}>
          {`${campaign?.formattedStartedAt} 〜 ${campaign?.formattedEndedAt}`}
        </Typography>
        <Typography variant="subtitle1" sx={{ pt: 4 }}>
          {t('entryConditions')}
        </Typography>
        <Typography variant="body2" sx={{ pt: 1 }}>
          {campaign?.entryCondition?.description}
        </Typography>
      </Stack>
      {hasAvatarImage ? (
        <Stack alignItems="center" spacing={2} sx={{ pt: 2 }}>
          {eyecatch ? (
            <Fade>
              <Avatar
                src={eyecatch}
                variant="square"
                sx={{ width: 220, height: 165 }}
              />
            </Fade>
          ) : (
            <Skeleton variant="rectangular" width={220} height={165} />
          )}
          <Typography variant="subtitle2">{t('eyeCatchingImage')}</Typography>
        </Stack>
      ) : (
        <NoProfileImageCell />
      )}
    </CellFrame>
  )
}

const CampaignDescriptionSkeletonCell: Cell = () => (
  <CellFrame sx={{ px: 5, pt: 4 }}>
    <SkeletonText variant="large" width="100%" />
    <Stack spacing={4}>
      <Stack spacing={1}>
        <SkeletonText variant="medium" width="50%" />
        <Stack>
          <SkeletonText variant="medium" numberOfLine={5} lastWidth="75%" />
        </Stack>
      </Stack>
      <Stack spacing={1}>
        <SkeletonText variant="medium" width="50%" />
        <SkeletonText variant="medium" width="100%" />
      </Stack>
      <Stack spacing={1}>
        <SkeletonText variant="medium" width="50%" />
        <SkeletonText variant="medium" numberOfLine={3} lastWidth="75%" />
      </Stack>
      <Stack flex={1} alignItems="center" justifyContent="center">
        <Stack sx={{ pt: 2, width: 220 }} spacing={2}>
          <Skeleton variant="rectangular" width="100%" height={165} />
          <SkeletonText variant="small" width="100%" />
        </Stack>
      </Stack>
    </Stack>
  </CellFrame>
)

const CampaignItemsSkeletonCell: Cell = () => {
  const { t } = useTranslation('consoleCampaignDetail')
  const items = useMemo(() => generateItems(t), [t])
  return (
    <CellFrame sx={{ px: 5, py: 3, mt: 3 }}>
      <Stack spacing={5}>
        {items.map((item, index) => (
          <ConsoleCampaignItemBox
            key={index}
            item={item}
            onUploadFile={async () => {}}
            onRemoveFile={() => {}}
          />
        ))}
      </Stack>
    </CellFrame>
  )
}

const FixedButtonCell: Cell = () => {
  const { t } = useTranslation('consoleCampaignDetail')
  return (
    <BottomSafeArea sx={{ bgcolor: 'grey.50' }}>
      <CellFrame sx={{ bgcolor: 'grey.50', py: 4, textAlign: 'center' }}>
        <Button
          form="campaign-item-form"
          type="submit"
          size="large"
          color="primary"
          sx={{ width: 244 }}
        >
          {t('registerAndEnter')}
        </Button>
      </CellFrame>
    </BottomSafeArea>
  )
}

function generateItems(t: TFunction): ConsoleCampaignItem[] {
  const rarities = [
    'superrare_1',
    'rare_1',
    'common_1',
    'common_2',
    'common_3',
    'common_4',
  ]
  return rarities.map(rarityValue => {
    const [rarity, index] = rarityValue.split('_') as [
      'common' | 'rare' | 'superrare',
      string
    ]
    return {
      rarity: {
        id: rarity,
        name: `${t(rarity)}_${index}`,
      },
      hasBenefit: rarity === 'superrare' || rarity === 'rare',
      benefit: {
        uri: '',
        originalPath: '',
        type: FileType.Audio,
      },
      image: {
        front: {
          fileUrl: '',
          tmpPath: '',
        },
        back: { uri: '' },
      },
    }
  })
}

const NoProfileImageCell: Cell = () => {
  const { t } = useTranslation('consoleCampaignDetail')
  const { routerPages } = useRouter()

  return (
    <CellFrame>
      <Stack
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        sx={{
          py: 4,
          mt: 4,
          mb: 8,
          bgcolor: 'grey.50',
          borderRadius: 3,
        }}
      >
        <Stack flexDirection="row" alignItems="center">
          <Icons.Error color="error" />
          <Typography variant="subtitle2" color="error" sx={{ ml: 1 }}>
            {t('noProfileImageDescription')}
          </Typography>
        </Stack>

        <Button
          size="large"
          sx={{ mt: 3 }}
          href={routerPages.ConsoleMyProfileEdit}
        >
          {t('profileImageRegister')}
        </Button>
      </Stack>
    </CellFrame>
  )
}
